import { useState, useMemo, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useAuth } from '@helios/auth-firebase-web';
import {
  NavIcon,
  useBrandTokens,
  PageHeader,
  type ShellOutletContext,
} from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { cloneFormDefinition } from '../../../services/form-clone-service';
import {
  qFormDefinitions,
  parseFieldsSchemaV1,
  flattenFields,
  qActiveFormByPurpose,
  type FormDefinitionRow,
} from '@helios/data-access';

// ─── Status Badge ───

type FormStatus = 'draft' | 'published' | 'archived';

const STATUS_STYLES: Record<FormStatus, { bg: string; color: string; label: string }> = {
  draft: { bg: 'var(--color-warning-bg)', color: 'var(--color-warning-text)', label: 'Draft' },
  published: {
    bg: 'var(--color-success-bg)',
    color: 'var(--color-success-text)',
    label: 'Published',
  },
  archived: { bg: 'var(--color-neutral-bg)', color: 'var(--text-muted)', label: 'Archived' },
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[(status as FormStatus) ?? 'draft'] ?? STATUS_STYLES.draft;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: s.bg,
        color: s.color,
        fontSize: 'var(--font-size-sm)',
        fontWeight: 'var(--font-weight-medium)',
        lineHeight: '20px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─── Column widths ───

const COL_WIDTHS = {
  name: '30%',
  status: '12%',
  version: '10%',
  updated: '20%',
  actions: '28%',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ─── Main Component ───

export interface FormsListPageProps {
  /**
   * PRD.305 — render the LABORATORY view of the same form catalogue: only
   * `lab_work` forms, and a New Form that starts as one. The definitions,
   * wizard and writes are shared with Clinic Forms; this narrows what is
   * listed, never what may be edited.
   */
  labOnly?: boolean;
}

export function FormsListPage({ labOnly = false }: FormsListPageProps = {}) {
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {};
  const orgId = claims?.activeOrg ?? '';

  // Every navigation out of this list stays in the shell the list is rendered
  // in. Sending the Laboratory view to /clinic/forms would drop the user into
  // the Patients sidebar mid-task.
  const formsBase = labOnly ? '/laboratory/forms' : '/clinic/forms';

  /**
   * `cloneFormDefinition` deliberately drops `purpose` — copying `registration`
   * would queue a second claimant to a live workflow. That reasoning does not
   * apply to Lab Work, which many forms hold at once and which is assigned per
   * service, so the Laboratory view restores it. Without this a copy vanishes
   * from the list that made it, which reads as the copy having failed.
   */
  const applyLabPurpose = useCallback(
    async (formDefinitionId: string) => {
      if (!labOnly || !db) return;
      await db.execute(`UPDATE form_definitions SET purpose = 'lab_work' WHERE id = ?`, [
        formDefinitionId,
      ]);
    },
    [labOnly, db],
  );

  // PRD.321 — Create Form dialog (Blank vs Start From Existing).
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: formRows = [] } = usePowerSyncQuery<FormDefinitionRow>(qFormDefinitions(orgId));
  const { data: activeRegRows = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qActiveFormByPurpose(orgId, 'registration'),
  );
  const activeRegistrationFormId = activeRegRows[0]?.id ?? null;

  const filtered = useMemo(() => {
    // The lab view narrows to Lab Work forms — the same `purpose` the result
    // drawer resolves assignments through (`qLabWorkFormsForService`).
    let rows = labOnly ? formRows.filter((r) => r.purpose === 'lab_work') : formRows;
    if (statusFilter !== 'all') {
      rows = rows.filter((r) => r.status === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }
    return rows;
  }, [formRows, search, statusFilter, labOnly]);

  /**
   * Create a blank form. UNCHANGED behaviour — this is exactly what the
   * "New Form" button did before PRD.321, now reached through the dialog's
   * "Blank Form" option (PRD §6.1, §25).
   */
  const createBlankForm = useCallback(
    async (name: string) => {
      if (!db || !orgId) return;
      const id = crypto.randomUUID();
      // Created from the Laboratory view, a form is a Lab Work form from the
      // start. Left null it would disappear from this list the moment it was
      // saved, which reads as the save having failed.
      await db.execute(
        `INSERT INTO form_definitions (id, org_id, name, status, version, fields_schema, purpose, created_at, updated_at)
         VALUES (?, ?, ?, 'draft', 1, '[]', ?, datetime('now'), datetime('now'))`,
        [id, orgId, name.trim() || 'Untitled Form', labOnly ? 'lab_work' : null],
      );
      navigate(`${formsBase}/${id}?step=1`);
    },
    [db, orgId, navigate, labOnly, formsBase],
  );

  /** Create a form by copying an existing one (PRD §6.2). */
  const createFromExisting = useCallback(
    async (name: string, sourceFormId: string) => {
      if (!db || !orgId) return;
      const { formDefinitionId } = await cloneFormDefinition(db, {
        orgId,
        sourceFormId,
        name,
      });
      // Any source form is allowed: basing a lab form on an existing template
      // is the point of cloning.
      await applyLabPurpose(formDefinitionId);
      navigate(`${formsBase}/${formDefinitionId}?step=1`);
    },
    [db, orgId, navigate, formsBase, applyLabPurpose],
  );

  const handleNewForm = useCallback(() => setCreateOpen(true), []);

  /**
   * PRD.321 — both copy entry points go through ONE implementation.
   *
   * The field/page/mapping copying that used to live inline here now lives in
   * `cloneFormDefinition`, so "Duplicate", "Edit (Copy)" and the new
   * "Start from existing form" cannot drift apart.
   */
  const handleDuplicate = useCallback(
    async (form: FormDefinitionRow, openAfter = false) => {
      if (!db || !orgId) return;
      try {
        const { formDefinitionId } = await cloneFormDefinition(db, {
          orgId,
          sourceFormId: form.id,
          name: `${form.name} (Copy)`,
        });
        await applyLabPurpose(formDefinitionId);
        if (openAfter) navigate(`${formsBase}/${formDefinitionId}?step=1`);
      } catch (err) {
        console.error('[forms] duplicate failed', err);
        setCloneError(err instanceof Error ? err.message : 'Could not copy this form.');
      }
    },
    [db, orgId, navigate],
  );

  const handlePublish = useCallback(
    async (form: FormDefinitionRow) => {
      if (!db) return;
      await db.execute(
        `UPDATE form_definitions SET status = 'published', updated_at = datetime('now') WHERE id = ?`,
        [form.id],
      );
    },
    [db],
  );

  const handleArchive = useCallback(
    async (form: FormDefinitionRow) => {
      if (!db) return;
      await db.execute(
        `UPDATE form_definitions SET status = 'archived', updated_at = datetime('now') WHERE id = ?`,
        [form.id],
      );
    },
    [db],
  );

  const handleRestore = useCallback(
    async (form: FormDefinitionRow) => {
      if (!db) return;
      await db.execute(
        `UPDATE form_definitions SET status = 'draft', updated_at = datetime('now') WHERE id = ?`,
        [form.id],
      );
    },
    [db],
  );

  const handleDelete = useCallback(
    async (form: FormDefinitionRow) => {
      if (!db) return;
      // Delete mappings, pages, then definition
      await db.execute(`DELETE FROM form_template_mappings WHERE form_definition_id = ?`, [
        form.id,
      ]);
      await db.execute(`DELETE FROM form_template_pages WHERE form_definition_id = ?`, [form.id]);
      await db.execute(`DELETE FROM form_definitions WHERE id = ?`, [form.id]);
    },
    [db],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <PageHeader
        title={labOnly ? 'Lab Work Forms' : 'Forms'}
        subtitle={
          labOnly
            ? 'Structured result forms that can be assigned to laboratory tests'
            : 'Manage form templates for patient documents'
        }
        collapsed={collapsed}
        onToggle={onToggleSidebar}
      >
        <button
          onClick={handleNewForm}
          style={{
            background: primary,
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            padding: '0 16px',
            height: 38,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flexShrink: 0,
            fontFamily: 'var(--font-family)',
          }}
        >
          <NavIcon name="plus" />
          New Form
        </button>
      </PageHeader>

      {/* Search + Filters */}
      <div
        style={{
          padding: '0 var(--sp-5) var(--sp-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ position: 'relative', flex: 1 }}>
          <span
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
              display: 'flex',
            }}
          >
            <NavIcon name="search" />
          </span>
          <input
            type="text"
            placeholder="Search forms..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              height: 38,
              paddingLeft: 36,
              paddingRight: 12,
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text)',
              outline: 'none',
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            height: 38,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text)',
            padding: '0 12px',
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="all">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 var(--sp-5) var(--sp-5)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: COL_WIDTHS.name }} />
            <col style={{ width: COL_WIDTHS.status }} />
            <col style={{ width: COL_WIDTHS.version }} />
            <col style={{ width: COL_WIDTHS.updated }} />
            <col style={{ width: COL_WIDTHS.actions }} />
          </colgroup>
          <thead>
            <tr>
              {['Name', 'Status', 'Version', 'Last Updated', 'Actions'].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid var(--border)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((form) => (
              <tr
                key={form.id}
                style={{ cursor: 'pointer' }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg-hover)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLTableRowElement).style.background = '';
                }}
              >
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                  onClick={() => {
                    if (form.status === 'published') {
                      handleDuplicate(form, true);
                    } else if (form.status === 'draft') {
                      navigate(`${formsBase}/${form.id}?step=1`);
                    }
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-medium)',
                      color: 'var(--text)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {form.name}
                    {form.purpose === 'registration' &&
                      (() => {
                        const isActive = form.id === activeRegistrationFormId;
                        return (
                          <span
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              fontWeight: 'var(--font-weight-medium)',
                              color: isActive ? '#16a34a' : '#9ca3af',
                              background: isActive ? '#f0fdf4' : '#f9fafb',
                              border: `1px solid ${isActive ? '#bbf7d0' : '#e5e7eb'}`,
                              borderRadius: 'var(--radius-sm)',
                              padding: '1px 6px',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Registration {isActive ? '(Active)' : '(Inactive)'}
                          </span>
                        );
                      })()}
                  </div>
                  {form.description && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text-muted)',
                        marginTop: 2,
                      }}
                    >
                      {form.description}
                    </div>
                  )}
                </td>
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <StatusBadge status={form.status} />
                </td>
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                    fontSize: 'var(--font-size-base)',
                    color: 'var(--text)',
                  }}
                >
                  v{form.version}
                </td>
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {formatDate(form.updated_at)}
                </td>
                <td
                  style={{
                    padding: '14px 12px',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <div style={{ display: 'flex', gap: 6 }}>
                    {form.status === 'draft' && (
                      <>
                        <ActionButton
                          label="Edit"
                          onClick={() => navigate(`${formsBase}/${form.id}?step=1`)}
                        />
                        <ActionButton label="Publish" onClick={() => handlePublish(form)} />
                        <ActionButton
                          label="Delete"
                          onClick={() => handleDelete(form)}
                          variant="danger"
                        />
                      </>
                    )}
                    {form.status === 'published' && (
                      <>
                        <ActionButton
                          label="Edit (Copy)"
                          onClick={() => handleDuplicate(form, true)}
                        />
                        <ActionButton label="Archive" onClick={() => handleArchive(form)} />
                      </>
                    )}
                    {form.status === 'archived' && (
                      <>
                        <ActionButton label="Restore" onClick={() => handleRestore(form)} />
                        <ActionButton label="Duplicate" onClick={() => handleDuplicate(form)} />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: 40,
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  {formRows.length === 0
                    ? 'No forms yet. Click "New Form" to create one.'
                    : 'No forms match your filter.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PRD.321 — a copy that failed must say so; silently doing nothing
          would read as a broken button. */}
      {cloneError && (
        <div
          role="alert"
          style={{
            marginTop: 'var(--sp-3)',
            padding: 'var(--sp-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-danger, #dc2626)',
            color: 'var(--color-danger, #dc2626)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {cloneError}
        </div>
      )}

      {createOpen && (
        <CreateFormDialog
          forms={formRows}
          onBlank={async (name) => {
            await createBlankForm(name);
            setCreateOpen(false);
          }}
          onFromExisting={async (name, sourceFormId) => {
            await createFromExisting(name, sourceFormId);
            setCreateOpen(false);
          }}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}

function ActionButton({
  label,
  onClick,
  variant,
}: {
  label: string;
  onClick: () => void;
  variant?: 'danger';
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        height: 28,
        padding: '0 10px',
        borderRadius: 'var(--radius-sm)',
        border: variant === 'danger' ? '1px solid #fecaca' : '1px solid var(--border)',
        background: variant === 'danger' ? '#fef2f2' : 'var(--bg-surface)',
        color: variant === 'danger' ? '#dc2626' : 'var(--text)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-medium)',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

// ─── PRD.321 — Create Form dialog ───

/**
 * Summary counts for the selected source, so an admin can tell two
 * similarly-named consent forms apart before copying one (PRD §6.2).
 *
 * Derived from `fields_schema` alone — deliberately NOT a preview subsystem.
 * Template page counts are omitted because they live in another table and
 * would need a per-source query the dialog does not otherwise issue.
 */
function summarizeSource(form: FormDefinitionRow | undefined): string | null {
  if (!form) return null;
  const schema = parseFieldsSchemaV1(form.fields_schema);
  const fields = flattenFields(schema);
  const signatures = fields.filter((f) => f.type === 'signature').length;
  const parts = [
    `${fields.length} field${fields.length === 1 ? '' : 's'}`,
    `${schema.sections.length} section${schema.sections.length === 1 ? '' : 's'}`,
  ];
  if (signatures > 0) parts.push(`${signatures} signature${signatures === 1 ? '' : 's'}`);
  if (form.default_view === 'render') parts.push('template pages');
  return parts.join(' · ');
}

function CreateFormDialog({
  forms,
  onBlank,
  onFromExisting,
  onClose,
}: {
  forms: FormDefinitionRow[];
  onBlank: (name: string) => Promise<void>;
  onFromExisting: (name: string, sourceId: string) => Promise<void>;
  onClose: () => void;
}) {
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';

  const [name, setName] = useState('');
  const [startFrom, setStartFrom] = useState<'blank' | 'existing'>('blank');
  const [sourceId, setSourceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Eligible sources.
   *
   * Archived forms are excluded (PRD §23) — they are retired, and the list
   * already offers an explicit Restore/Duplicate path for them. Drafts stay
   * eligible: the whole point is building one good form and branching from it,
   * and a clinic should not have to publish a form to reuse it.
   *
   * `qFormDefinitions` is already org-scoped, so cross-org sources can never
   * appear here; the service re-checks anyway rather than trusting the UI.
   */
  const eligible = useMemo(
    () => forms.filter((f) => f.status !== 'archived').sort((a, b) => a.name.localeCompare(b.name)),
    [forms],
  );

  const selected = eligible.find((f) => f.id === sourceId);
  const summary = summarizeSource(selected);
  const canCreate = startFrom === 'blank' || Boolean(sourceId);

  async function submit() {
    if (!canCreate || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (startFrom === 'existing') {
        await onFromExisting(name, sourceId);
      } else {
        await onBlank(name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the form.');
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: 'var(--sp-4)',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        style={{
          width: 460,
          maxWidth: '100%',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          padding: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 'var(--font-weight-semi)',
          }}
        >
          Create Form
        </h2>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Untitled Form"
            style={{
              padding: '8px 10px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'inherit',
            }}
          />
        </label>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Start From
          </span>
          {(
            [
              { value: 'blank', label: 'Blank Form' },
              { value: 'existing', label: 'Existing Form' },
            ] as const
          ).map((opt) => (
            <label
              key={opt.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="start-from"
                checked={startFrom === opt.value}
                onChange={() => setStartFrom(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>

        {startFrom === 'existing' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              Existing Form
            </span>
            <select
              value={sourceId}
              onChange={(e) => setSourceId(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'inherit',
              }}
            >
              <option value="">Select a form…</option>
              {eligible.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                  {f.status === 'draft' ? ' (Draft)' : ''}
                </option>
              ))}
            </select>
            {summary && (
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                {summary}
              </span>
            )}
            {/* Wording avoids "inherit"/"linked"/"synced" — the copy is a
                starting point with no ongoing dependency (PRD §24). */}
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                lineHeight: 1.5,
              }}
            >
              Copies the selected form&apos;s fields and layout as a starting point. Template pages
              and field positions are copied too. The new form can be changed independently.
            </span>
          </div>
        )}

        {error && (
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger, #dc2626)' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canCreate || busy}
            style={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: canCreate && !busy ? primary : 'var(--border)',
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: canCreate && !busy ? 'pointer' : 'default',
            }}
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
